export type PriceTagSizeUnit = "g" | "kg" | "ml" | "l" | "pcs";

export interface ParsedPriceTagSize {
  value: number;
  unit: PriceTagSizeUnit;
}

export interface ParsedPriceTagUnitPrice {
  value: number;
  unit: "kg" | "l" | "pcs";
}

export interface ParsedPriceTag {
  brand?: string;
  productName?: string;
  size?: ParsedPriceTagSize;
  regularPrice?: number;
  loyaltyPrice?: number;
  promoPrice?: number;
  unitPrice?: ParsedPriceTagUnitPrice;
  loyaltyProgram?: string;
  barcode?: string;
  internalProductCode?: string;
  rawText: string;
}

export interface ParsePriceTagOptions {
  storeId?: string;
  detectedBarcode?: string;
  detectedInternalCode?: string;
}

export function isUsableParsedTag(tag: ParsedPriceTag): boolean {
  if (tag.barcode && tag.barcode.length >= 8) return true;
  if (tag.internalProductCode && tag.internalProductCode.length >= 4) return true;
  const name = (tag.productName ?? "").replace(/\s+/g, " ").trim();
  return name.length >= 4;
}

/** Local parse quality only — catalog matching still happens on the agent. */
export function estimateClientConfidence(tag: ParsedPriceTag): number {
  let score = 0;
  if (tag.barcode && tag.barcode.length >= 8) score += 0.45;
  const name = (tag.productName ?? "").trim();
  if (name.length >= 8) score += 0.25;
  else if (name.length >= 4) score += 0.12;
  if (tag.regularPrice != null || tag.loyaltyPrice != null || tag.promoPrice != null) score += 0.15;
  if (tag.size) score += 0.1;
  if (tag.brand) score += 0.05;
  return Math.min(1, Number(score.toFixed(2)));
}

export function toPriceTagResolvePayload(
  tag: ParsedPriceTag,
  opts?: { storeId?: string; productId?: string },
): {
  storeId?: string;
  rawText: string;
  barcode?: string;
  parsed: Omit<ParsedPriceTag, "rawText" | "barcode">;
  clientConfidence: number;
  productId?: string;
} {
  return {
    storeId: opts?.storeId,
    rawText: tag.rawText,
    barcode: tag.barcode,
    parsed: {
      brand: tag.brand,
      productName: tag.productName,
      size: tag.size,
      regularPrice: tag.regularPrice,
      promoPrice: tag.promoPrice,
      loyaltyPrice: tag.loyaltyPrice,
      unitPrice: tag.unitPrice,
      loyaltyProgram: tag.loyaltyProgram,
      internalProductCode: tag.internalProductCode,
    },
    clientConfidence: estimateClientConfidence(tag),
    productId: opts?.productId,
  };
}
